import React, { useState } from 'react';
import { captureUpgradeInterest } from '../lib/trialLimits';

// ──────────────────────────────────────────────────────────────────────
// UpgradeModal — shown when a trial limit is hit.
// Captures email interest into upgrade_interest table, then shows
// success state. Same modal for all 3 modes — caller passes
// subjectLabel + yearLabel + reason for personalisation.
// ──────────────────────────────────────────────────────────────────────

const T = {
  bg:       'rgba(0, 0, 0, 0.55)',
  card:     '#FFFFFF',
  border:   '#E5DCC1',
  text:     '#3A2A00',
  textMid:  '#7A6B47',
  accent:   '#0B8A7E',
  accentLo: '#E6F4F1',
  surface:  '#FBF5E5',
  error:    '#DC2626',
  success:  '#10B981',
};

export default function UpgradeModal({
  reason = 'practice',          // 'practice' | 'exam' | 'learn'
  yearLabel = '',
  subjectLabel = '',
  userEmail = '',
  userId,
  onDismiss,
}) {
  const [email, setEmail] = useState(userEmail || '');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const heading = reason === 'learn'
    ? `You've used your free Learn topic for ${subjectLabel || 'this subject'}.`
    : `You've reached your free trial limit in ${subjectLabel || 'this subject'}.`;
  const subline = reason === 'learn'
    ? `Free trial includes one Learn topic per (year, subject). Upgrade to unlock every topic.`
    : `Free trial includes 10 ${reason} questions per (year, subject). Upgrade for unlimited access.`;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!email || !email.includes('@')) { setError('Enter a valid email.'); return; }
    setSubmitting(true);
    try {
      const res = await captureUpgradeInterest({ userId, email: email.trim() });
      if (res.ok) setDone(true);
      else setError(res.error || "Couldn't record your email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Upgrade to continue"
      style={{
        position: 'fixed', inset: 0, background: T.bg, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}>
      <div style={{
        width: '100%', maxWidth: 440, background: T.card,
        border: `1px solid ${T.border}`, borderRadius: 20,
        padding: '24px 24px 22px', boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{
            background: T.accentLo, color: T.accent, fontSize: 11,
            fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
            padding: '4px 10px', borderRadius: 99,
          }}>{yearLabel || 'Free trial'}</div>
          <button onClick={onDismiss} aria-label="Close" style={{
            background: 'none', border: 'none', color: T.textMid,
            cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 0,
          }}>×</button>
        </div>

        {!done && (
          <>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif", fontSize: 22,
              color: T.text, margin: '6px 0 6px',
            }}>
              {heading}
            </h2>
            <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.55, margin: '0 0 16px' }}>
              {subline} Paid plans aren't live yet — drop your email and we'll let you know the moment they are.
            </p>
            <form onSubmit={submit}>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Your email"
                style={{
                  width: '100%', padding: '12px 14px',
                  background: T.surface, border: `1.5px solid ${T.border}`,
                  borderRadius: 12, fontSize: 15, color: T.text,
                  outline: 'none', boxSizing: 'border-box', marginBottom: 10,
                  fontFamily: 'inherit',
                }} required />
              {error && <div style={{ color: T.error, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{error}</div>}
              <button type="submit" disabled={submitting} style={{
                width: '100%', padding: 13,
                background: submitting ? '#9CA3AF' : T.accent, color: 'white',
                border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 15,
                cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}>
                {submitting ? 'Saving…' : 'Notify me when paid plans launch'}
              </button>
            </form>
            <button onClick={onDismiss} style={{
              display: 'block', width: '100%', marginTop: 10,
              background: 'none', border: 'none', color: T.textMid,
              padding: '6px 0', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Continue browsing
            </button>
          </>
        )}

        {done && (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: T.success + '22', color: T.success,
              fontSize: 28, fontWeight: 900,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>✓</div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, color: T.text, margin: '0 0 6px' }}>
              Thanks — you're on the list.
            </h2>
            <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.5, margin: '0 0 16px' }}>
              We'll email <strong>{email}</strong> when paid plans launch. In the meantime, you can keep using everything you've already unlocked.
            </p>
            <button onClick={onDismiss} style={{
              padding: '10px 18px', background: T.accent, color: 'white',
              border: 'none', borderRadius: 10, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
            }}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
